




































































import { Component, Vue } from 'vue-property-decorator';
import { UserApi } from '@/api/user-api';
import { namespace } from 'vuex-class';
import { Forbidden } from '@/models/forbidden-error';
import { User } from '@/models/user';
import { CreateUserRequest } from '@/models/create-user-request';
import loading from '@/components/loading.vue';
import adminCreateUserModal from '@/components/admin-create-user-modal.vue';
import adminEditPermissionsModal from '@/components/admin-user-edit-permissions-modal.vue';
import adminEditRoleModal from '@/components/admin-user-edit-role-modal.vue';
import { SitePermission } from '@/models/site-permission';
import { CustomerApi } from '@/api/customer-api';
import { kebabcase } from '@/filters/kebab_case';

const userModule = namespace('user');
const userApi = new UserApi();
const customerApi = new CustomerApi();

@Component({
    components: {
        loading,
        adminCreateUserModal,
        adminEditPermissionsModal,
        adminEditRoleModal,
    },
})
export default class AdminUsers extends Vue {
    @userModule.Getter('isSystem') public currentUserIsSystem: boolean;
    @userModule.Getter('userIsSystem') public userIsSystem: (user: User) => boolean;
    @userModule.Getter('userIsAdmin') public userIsAdmin: (user: User) => boolean;
    public isLoading: boolean = true;
    public users: Array<User> = new Array<User>();
    public showEditPermissionsModal: boolean = false;
    public showEditRolesModal: boolean = false;
    public selectedUser: User | null = null;
    public saving: boolean = false;

    public editRoles(user: User): void {
        this.selectedUser = user;
        this.showEditRolesModal = true;
    }
    public editPermissions(user: User): void {
        this.selectedUser = user;
        this.showEditPermissionsModal = true;
    }
    public async handlePermissionsSaved(customersToGrant: Array<{ id: string }>, customersToRevoke: Array<{ id: string }>, sitesToGrant: Array<{ id: string, customerId: string }>, sitesToRevoke: Array<{ id: string, customerId: string }>, permissionsToGrant: Array<{ id: string, customerId: string, siteId: string }>, permissionsToRevoke: Array<{ id: string, customerId: string, siteId: string }>): Promise<void> {
        if (this.saving) {
            return;
        }
        this.saving = true;
        const customerRequests: Array<Promise<void>> = [];
        const siteRequests: Array<Promise<void>> = [];
        const permissionRequests: Array<Promise<void>> = [];

        for (const customer of customersToGrant) {
            customerRequests.push(customerApi.grantCustomerAccessAsync(customer.id, this.selectedUser!.id!));
        }
        for (const customer of customersToRevoke) {
            customerRequests.push(customerApi.revokeCustomerAccessAsync(customer.id, this.selectedUser!.id!));
        }
        await Promise.all(customerRequests);
        for (const site of sitesToGrant) {
            siteRequests.push(customerApi.grantSiteAccessAsync(site.customerId, site.id, this.selectedUser!.id!));
        }
        for (const site of sitesToRevoke) {
            siteRequests.push(customerApi.revokeSiteAccessAsync(site.customerId, site.id, this.selectedUser!.id!));
        }
        await Promise.all(siteRequests);
        for (const permission of permissionsToGrant) {
            permissionRequests.push(customerApi.grantSitePermissionAsync(permission.customerId, permission.siteId, permission.id, this.selectedUser!.id!));
        }
        for (const permission of permissionsToRevoke) {
            permissionRequests.push(customerApi.revokeSitePermissionAsync(permission.customerId, permission.siteId, permission.id, this.selectedUser!.id!));
        }
        await Promise.all(permissionRequests);

        this.onCancel();
        this.saving = false;
        this.isLoading = true;
        await this.load();
        this.isLoading = false;
    }
    public async handleRolesSaved(rolesToGrant: Array<{ id: string, name: string }>, rolesToRevoke: Array<{ id: string, name: string }>): Promise<void> {
        if (this.saving) {
            return;
        }
        this.saving = true;
        const roleRequests: Array<Promise<void>> = [];

        for (const role of rolesToGrant) {
            roleRequests.push(userApi.grantUserRoleAsync(this.selectedUser!.id!, kebabcase(role.name.split(':')[1])));
        }
        for (const role of rolesToRevoke) {
            roleRequests.push(userApi.revokeUserRoleAsync(this.selectedUser!.id!, kebabcase(role.name.split(':')[1])));
        }
        await Promise.all(roleRequests);

        this.onCancel();
        this.saving = false;
        this.isLoading = true;
        await this.load();
        this.isLoading = false;
    }
    public onCancel(): void {
        this.selectedUser = null;
        this.showEditPermissionsModal = false;
        this.showEditRolesModal = false;
    }
    public async load(): Promise<void> {
        try {
            const users = await userApi.fetchAsync();
            this.users = users;
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: 'forbidden' });
            }
        }
    }
    public async mounted(): Promise<void> {
        this.isLoading = true;
        await this.load();
        this.isLoading = false;
    }
}
